import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { signInWithPopup, signOut } from "firebase/auth";
import { toast } from "sonner";
import { firebaseAuth, googleProvider } from "../lib/firebase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "qp_session_token";

axios.defaults.withCredentials = true;

// Auth no longer depends solely on the session cookie surviving a refresh
// (it wasn't, reliably, across the Firebase Hosting -> Cloud Run rewrite).
// The session token is also kept in localStorage and sent as a Bearer
// header on every request, which the backend already accepts as a fallback.
const storedToken = (() => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (e) {
    return null;
  }
})();
if (storedToken) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
}

const setStoredToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      localStorage.removeItem(TOKEN_KEY);
      delete axios.defaults.headers.common["Authorization"];
    }
  } catch (e) {
    // ignore (e.g. localStorage unavailable)
  }
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch (e) {
      setStoredToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Signs in with Google via Firebase Authentication (a popup, not a
  // redirect to a third-party site), then exchanges the resulting Firebase
  // ID token for our own session token.
  const login = async () => {
    try {
      const result = await signInWithPopup(firebaseAuth, googleProvider);
      const idToken = await result.user.getIdToken();
      const res = await axios.post(`${API}/auth/session`, { id_token: idToken }, { withCredentials: true });
      setStoredToken(res.data.session_token);
      // The backend may have just set this user's Online References level
      // (a Firebase custom claim); refresh the ID token so Firestore sees it now.
      if (res.data.ref_claims_updated) {
        await result.user.getIdToken(true);
      }
      if (res.data.ref_claims_error) {
        toast.error(`Could not set your Online References access level: ${res.data.ref_claims_error}`);
      }
      const { session_token, ...userData } = res.data;
      setUser(userData);
      return userData;
    } catch (err) {
      if (err.code === "auth/popup-closed-by-user" || err.code === "auth/cancelled-popup-request") {
        return null;
      }
      console.error("Sign-in failed:", err);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // ignore
    }
    try {
      await signOut(firebaseAuth);
    } catch (e) {
      // ignore
    }
    setStoredToken(null);
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
